// AscendStatusCard — operator-facing surface for the autonomous
// onboarding pipeline. Polls /api/ascend/status every 5s and shows
// the per-step timeline so the operator can see ASCEND working
// without wondering "is something happening?"
//
// Visible only when there's an active run that hasn't reached
// `ready`. Once ready, the card hides itself (returns null) so the
// dashboard isn't permanently cluttered. needs_human runs stay
// visible because the operator needs to act.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Loader2, CheckCircle2, AlertTriangle, Clock, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';

const STEP_LABELS = {
  welcome_touch:      'Welcoming you in',
  number_provisioned: 'Provisioning your phone number',
  a2p_submitted:      'Registering your A2P brand',
  templates_seeded:   'Loading your SMS templates',
  carrier_defaults:   'Setting up your carrier defaults',
};

// Turn a raw step error code into a human phrase for the inline hint.
// Unknown codes fall through to the raw string (better a code than
// nothing when a new failure mode appears).
const FRIENDLY_HINTS = {
  business_info_required: 'needs your business info',
};
function friendlyHint(code) {
  if (!code) return null;
  return FRIENDLY_HINTS[code] || code;
}

// The provisioning step returns this error while it waits on the
// operator's business name + address. Detect it so we can show an
// explicit, actionable prompt instead of a cryptic spinning row.
function isBlockedOnBusinessInfo(step) {
  if (!step) return false;
  const blocking = step.status === 'failed_retryable' || step.status === 'failed';
  return blocking && String(step.last_error || '').includes('business_info_required');
}

const STEP_ORDER = [
  'welcome_touch',
  'number_provisioned',
  'a2p_submitted',
  'templates_seeded',
  'carrier_defaults',
];

async function fetchStatus() {
  const jwt = readPersistedJwtDirect();
  const res = await fetch('/api/ascend/status', {
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
  });
  if (res.status === 404) return { noRun: true };
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

function StepRow({ stepKey, step }) {
  const label = STEP_LABELS[stepKey] || stepKey;
  const status = step?.status || 'pending';
  let icon, tone, hint;
  if (status === 'done') {
    icon = <CheckCircle2 size={14} className="text-emerald-600"/>;
    tone = 'text-slate-700';
    hint = step.payload?.stub ? 'stub' : step.payload?.synthetic ? 'demo' : null;
  } else if (status === 'failed_retryable') {
    icon = <Loader2 size={14} className="text-amber-600 animate-spin"/>;
    tone = 'text-amber-700';
    hint = friendlyHint(step.last_error) || 'retrying';
  } else if (status === 'failed') {
    icon = <AlertTriangle size={14} className="text-rose-600"/>;
    tone = 'text-rose-700';
    hint = friendlyHint(step.last_error) || 'needs attention';
  } else {
    icon = <Clock size={14} className="text-slate-400"/>;
    tone = 'text-slate-500';
    hint = null;
  }
  return (
    <li className="flex items-center gap-2 text-sm py-1">
      <span className="shrink-0">{icon}</span>
      <span className={`${tone} flex-1`}>{label}</span>
      {hint && <span className="text-[10px] text-slate-400 italic">{hint}</span>}
    </li>
  );
}

export default function AscendStatusCard({ onGotoTab } = {}) {
  const [run, setRun] = React.useState(null);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    let timer;
    const tick = async () => {
      try {
        const r = await fetchStatus();
        if (!alive) return;
        if (r.noRun) { setRun(null); return; }
        setRun(r.run);
        // Poll faster while in flight; back off once terminal.
        const terminal = r.run?.state === 'ready' || r.run?.state === 'cancelled';
        timer = setTimeout(tick, terminal ? 30_000 : 5_000);
      } catch (e) {
        if (!alive) return;
        setErr(e.message);
        timer = setTimeout(tick, 15_000);
      }
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, []);

  // No run yet (race window post-signup) or ready (success — hide).
  if (!run || run.state === 'ready' || run.state === 'cancelled') return null;

  // Is the pipeline specifically stalled waiting on the operator's
  // business name + address? If so we show an explicit "here's exactly
  // what's needed and how to fix it" prompt — this is the one blocker
  // the operator can resolve themselves, and a generic spinner would
  // leave them stuck retrying forever.
  const blockedOnBusinessInfo = isBlockedOnBusinessInfo((run.steps || {}).number_provisioned);

  const isNeedsHuman = run.state === 'needs_human';
  // A business-info stall takes visual priority over the generic
  // in-progress/needs-human framing — it's the actionable case.
  const headline = blockedOnBusinessInfo
    ? 'ASCEND needs your business name & address'
    : isNeedsHuman
      ? 'ASCEND needs you for one step'
      : 'ASCEND is setting up your account';
  const subline = blockedOnBusinessInfo
    ? 'Your business phone line can\'t be registered until we have these — carriers require them. Add them and setup resumes automatically within a minute.'
    : isNeedsHuman
      ? 'One step couldn\'t complete on its own — see what\'s waiting below.'
      : 'You can start exploring; AI is finishing setup in the background.';
  const showAlertStyle = blockedOnBusinessInfo || isNeedsHuman;

  return (
    <div className={`border rounded-xl p-4 mb-4 ${showAlertStyle ? 'bg-amber-50 border-amber-200' : 'bg-indigo-50/50 border-indigo-200'}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          {showAlertStyle
            ? <AlertTriangle size={20} className="text-amber-700"/>
            : <Loader2 size={20} className="text-indigo-700 animate-spin"/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">{headline}</div>
          <div className="text-xs text-slate-600 mt-0.5">{subline}</div>

          {blockedOnBusinessInfo && (
            <div className="mt-3 rounded-lg bg-white/70 border border-amber-200 px-3 py-2.5">
              <div className="text-xs font-semibold text-slate-700">What we need</div>
              <ul className="mt-1.5 space-y-1 text-xs text-slate-600">
                <li className="flex items-center gap-2">
                  <span className="text-amber-600" aria-hidden="true">•</span>
                  Business name (e.g. Curry Coverage LLC)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-amber-600" aria-hidden="true">•</span>
                  Business address (street, city, state, ZIP)
                </li>
              </ul>
              {onGotoTab && (
                <button
                  onClick={() => onGotoTab('settings')}
                  className="mt-2.5 inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-slate-900 text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm transition"
                >
                  Add business info in Settings
                  <ArrowRight size={13}/>
                </button>
              )}
            </div>
          )}

          <ul className="mt-3 divide-y divide-slate-100">
            {STEP_ORDER.map(k => <StepRow key={k} stepKey={k} step={(run.steps || {})[k]}/>)}
          </ul>
          {err && (
            <div className="text-[10px] text-rose-600 mt-2">Status check failed: {err}</div>
          )}
        </div>
      </div>
    </div>
  );
}
